import { exportPrisma, reportingPrisma } from "client";
import { REPORT_TYPE } from "services/attendeeReport";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";
import { buildOrderLookupSubpipeline } from "./orderLookupStages";

export function shouldUseEarlyOrderPagination(
  paginate: boolean,
  search: string | undefined
): boolean {
  return paginate && !search;
}

/** Matches rows strictly after `(createdAt DESC, orderId DESC)` composite cursor — same ordering as `$sort`. */
function buildOrderForwardCompositeMatch(
  orderPathSegment: string,
  cursorOidHex: string,
  cursorTsMs: number
): { $match: Record<string, unknown> } {
  const createdRef = `$${orderPathSegment}.createdAt`;
  const oidRef = `$${orderPathSegment}._id`;
  const cid = cursorOidHex.toLowerCase();
  return {
    $match: {
      $expr: {
        $or: [
          { $lt: [{ $toLong: createdRef }, cursorTsMs] },
          {
            $and: [
              { $eq: [{ $toLong: createdRef }, cursorTsMs] },
              {
                $lt: [
                  {
                    $toLower: {
                      $convert: {
                        input: oidRef,
                        to: "string",
                        onError: "",
                        onNull: "",
                      },
                    },
                  },
                  cid,
                ],
              },
            ],
          },
        ],
      },
    },
  };
}

/** Matches rows strictly before anchor in descending sort ("newer" chunk) for previous navigation. */
function buildOrderReverseCompositeMatch(
  orderPathSegment: string,
  beforeOidHex: string,
  beforeTsMs: number
): { $match: Record<string, unknown> } {
  const createdRef = `$${orderPathSegment}.createdAt`;
  const oidRef = `$${orderPathSegment}._id`;
  const bid = beforeOidHex.toLowerCase();
  return {
    $match: {
      $expr: {
        $or: [
          { $gt: [{ $toLong: createdRef }, beforeTsMs] },
          {
            $and: [
              { $eq: [{ $toLong: createdRef }, beforeTsMs] },
              {
                $gt: [
                  {
                    $toLower: {
                      $convert: {
                        input: oidRef,
                        to: "string",
                        onError: "",
                        onNull: "",
                      },
                    },
                  },
                  bid,
                ],
              },
            ],
          },
        ],
      },
    },
  };
}

export type OrderReportPaginationKeyset = {
  cursorId?: string;
  cursorTsMs?: number;
  beforeCursor?: string;
  beforeTsMs?: number;
};

// order report
export const orderReportData = async (
  id: any,
  limit: number,
  type: REPORT_TYPE,
  paginate: boolean = false,
  showExpireOrders: boolean = false,
  search?: string,
  lang?: string,
  deselectedColumns?: any,
  exported?: boolean,
  isDTCMEvent?: boolean,
  status?: string,
  ids?: string[],
  paginationKeyset?: OrderReportPaginationKeyset
) => {
  try {
    const prisma = exported ? exportPrisma : reportingPrisma;

    // Resolve timezone from the event when available. Fallback to UTC.
    let eventTimeZone = "UTC";
    if (type === REPORT_TYPE.EVENT && id) {
      const event = await prisma.event.findUnique({
        where: { id },
        select: { timezone: true },
      });
      eventTimeZone = event?.timezone || "UTC";
    }
    let filtrationStages: any[] = [];
    const isValidObjectId = (id: string) => {
      return /^[a-f\d]{24}$/i.test(id);
    };
    const searchAsNumber = !isNaN(Number(search)) ? Number(search) : null;
    const searchAsString = search
      ? search.toString().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      : null;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (search) {
      filtrationStages = [
        {
          $match: {
            $or: [
              isValidObjectId(search)
                ? { "order._id": { $oid: search } }
                : null,
              isValidObjectId(search)
                ? { "tickets._id": { $oid: search } }
                : null,

              searchAsNumber !== null
                ? { "order.number": searchAsNumber }
                : null,
              {
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $ifNull: [
                        { $toString: "$order.status" }, // Convert to string
                        "", // Fallback for null values
                      ],
                    },
                    options: "im",
                  },
                },
              },
              {
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $reduce: {
                        input: {
                          $ifNull: ["$orderItems.translations.name", []], // Handle null or missing arrays
                        },
                        initialValue: "",
                        in: {
                          $concat: ["$$value", " ", { $toString: "$$this" }], // Flatten and convert array to string
                        },
                      },
                    },
                    options: "im",
                  },
                },
              },
              {
                // Search across all form result fields (buyer name, email, phone, etc.)
                // IMPORTANT: $objectToArray throws if surveyResult is null — use $ifNull fallback
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $reduce: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        initialValue: "",
                        in: {
                          $concat: [
                            "$$value",
                            " ",
                            {
                              $convert: {
                                input: "$$this.v",
                                to: "string",
                                onError: "", // Safely ignore array/object fields like checkboxes
                                onNull: "",
                              },
                            },
                          ],
                        },
                      },
                    },
                    options: "im",
                  },
                },
              },
              // Search buyer name from contact (invitation-based orders)
              {
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $ifNull: [{ $toString: "$contact.name" }, ""],
                    },
                    options: "im",
                  },
                },
              },
              // Search buyer email from contact
              {
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $ifNull: [{ $toString: "$contact.email" }, ""],
                    },
                    options: "im",
                  },
                },
              },
              // Search buyer phone from contact
              {
                $expr: {
                  $regexMatch: {
                    regex: searchAsString,
                    input: {
                      $ifNull: [{ $toString: "$contact.phoneNumber" }, ""],
                    },
                    options: "im",
                  },
                },
              },
              // {
              //   $expr: {
              //     $regexMatch: {
              //       regex: searchAsString,
              //       input: {
              //         $ifNull: [
              //           { $toString: "$eventName" }, // Convert to string
              //           "", // Fallback for null values
              //         ],
              //       },
              //       options: "im",
              //     },
              //   },
              // },
            ].filter(Boolean), // Remove null conditions
          },
        },
      ];
    }

    const validOrderIdFilters =
      ids && ids.length > 0
        ? ids.filter((oid) => oid && isValidObjectId(oid))
        : [];

    const orderLookupPreMatch = buildOrderLookupSubpipeline({
      status,
      showExpireOrders,
      validOrderObjectIds: validOrderIdFilters,
    });

    const useEarlyOrderPagination = shouldUseEarlyOrderPagination(
      paginate,
      search
    );

    const ks = paginationKeyset ?? {};
    const fwdKeysetEligible =
      !!paginate &&
      limit > 0 &&
      !!ks.cursorId &&
      isValidObjectId(ks.cursorId) &&
      typeof ks.cursorTsMs === "number" &&
      Number.isFinite(ks.cursorTsMs);

    const revKeysetEligible =
      !!paginate &&
      limit > 0 &&
      !!ks.beforeCursor &&
      isValidObjectId(ks.beforeCursor) &&
      typeof ks.beforeTsMs === "number" &&
      Number.isFinite(ks.beforeTsMs);

    const useEarlyForwardKeyset =
      useEarlyOrderPagination && fwdKeysetEligible && !revKeysetEligible;
    const useEarlyReverseKeyset =
      useEarlyOrderPagination && revKeysetEligible && !fwdKeysetEligible;

    const useFooterForwardKeyset =
      !useEarlyOrderPagination &&
      !!paginate &&
      fwdKeysetEligible &&
      !revKeysetEligible;
    const useFooterReverseKeyset =
      !useEarlyOrderPagination &&
      !!paginate &&
      revKeysetEligible &&
      !fwdKeysetEligible;

    const pageLimit = paginate && limit > 0 ? limit + 1 : limit;

    const orderLookupStage = {
      $lookup: {
        from: "Order",
        let: {
          eventOid: "$_id",
          eventIdStr: { $toString: "$_id" },
        },
        pipeline: orderLookupPreMatch,
        as: "order",
      },
    };

    const stagesBeforeOrderItems = [
      orderLookupStage,
      {
        $unwind: {
          path: "$order",
        },
      },
      {
        $lookup: {
          from: "Payment",
          let: { orderId: "$order._id" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$orderId", "$$orderId"] },
              },
            },
            {
              $lookup: {
                from: "GatewayMethod",
                localField: "paymentMethodId",
                foreignField: "_id",
                as: "gatewayMethod",
              },
            },
            {
              $unwind: {
                path: "$gatewayMethod",
                preserveNullAndEmptyArrays: true,
              },
            },
            {
              $addFields: {
                method: "$gatewayMethod.type",
              },
            },
          ],
          as: "payment",
        },
      },
      {
        $lookup: {
          from: "Invoice",
          localField: "order._id",
          foreignField: "orderId",
          as: "invoice",
        },
      },
      {
        $unwind: {
          path: "$invoice",
          preserveNullAndEmptyArrays: true,
        },
      },
      { $addFields: { orderIdString: { $toString: "$order._id" } } },
      {
        $lookup: {
          from: "User",
          let: { createdByAdminId: "$order.createdByAdminId" },
          pipeline: [
            {
              $match: {
                $expr: {
                  $or: [
                    { $eq: ["$_id", "$$createdByAdminId"] },
                    {
                      $eq: [
                        { $toString: "$_id" },
                        { $toString: "$$createdByAdminId" },
                      ],
                    },
                  ],
                },
              },
            },
            { $limit: 1 },
          ],
          as: "createdByAdmin",
        },
      },
      {
        $unwind: {
          path: "$createdByAdmin",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResultEntity",
          localField: "order._id",
          foreignField: "orderId",
          as: "orderFormResultEntity",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "orderFormResultEntity.formResultId",
          foreignField: "_id",
          as: "orderFormResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormTemplate",
          localField: "orderFormResultEntity.formResult.formTemplateId",
          foreignField: "_id",
          as: "orderFormResultEntity.formResult.formTemplate",
        },
      },
      {
        $unwind: {
          path: "$orderFormResultEntity.formResult.formTemplate",
          preserveNullAndEmptyArrays: true,
        },
      },
    ];

    const stagesFromOrderItems = [
      {
        $lookup: {
          from: "OrderItem",
          localField: "order._id",
          foreignField: "orderId",
          as: "orderItems",
        },
      },
      {
        $unwind: "$orderItems",
      },
      { $addFields: { "orderItems.id": { $toString: "$orderItems._id" } } },
      {
        $lookup: {
          from: "FormResultEntity",
          localField: "orderItems._id",
          foreignField: "orderItemId",
          as: "formResultEntity",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormResult",
          localField: "formResultEntity.formResultId",
          foreignField: "_id",
          as: "formResultEntity.formResult",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity.formResult",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "FormTemplate",
          localField: "formResultEntity.formResult.formTemplateId",
          foreignField: "_id",
          as: "formResultEntity.formTemplate",
        },
      },
      {
        $unwind: {
          path: "$formResultEntity.formTemplate",
          preserveNullAndEmptyArrays: true,
        },
      },

      {
        $lookup: {
          from: "Ticket",
          localField: "orderItems.ticketId",
          foreignField: "_id",
          as: "orderItems.ticket",
        },
      },
      {
        $unwind: {
          path: "$orderItems.ticket",
          preserveNullAndEmptyArrays: true,
        },
      },
      ...(isDTCMEvent
        ? [
            {
              $lookup: {
                from: "DTCMTicketPrice",
                localField: "orderItems.ticket.dtcmTicketPriceId",
                foreignField: "_id",
                as: "orderItems.ticket.dtcmTicketPrice",
              },
            },
            {
              $unwind: {
                path: "$orderItems.ticket.dtcmTicketPrice",
                preserveNullAndEmptyArrays: true,
              },
            },
            {
              $lookup: {
                from: "DTCMPriceCategory",
                localField: "orderItems.ticket.dtcmTicketPrice.categoryId",
                foreignField: "_id",
                as: "orderItems.ticket.dtcmTicketPrice.category",
              },
            },
            {
              $unwind: {
                path: "$orderItems.ticket.dtcmTicketPrice.category",
                preserveNullAndEmptyArrays: true,
              },
            },
          ]
        : []),
      {
        $lookup: {
          from: "CheckInOut",
          let: { orderItemId: "$orderItems._id" },
          pipeline: [
            { $match: { $expr: { $eq: ["$orderItemId", "$$orderItemId"] } } },
            { $sort: { _id: -1 } },
            { $limit: 1 },
          ],
          as: "orderItems.checksInOut",
        },
      },
      {
        $unwind: {
          path: "$orderItems.checksInOut",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "TicketTranslations",
          localField: "orderItems.ticketId",
          foreignField: "ticketId",
          as: "orderItems.ticket.translations",
          pipeline: [
            {
              $lookup: {
                from: "Language",
                localField: "languageId",
                foreignField: "_id",
                as: "language",
              },
            },
            {
              $match: {
                "language.code": lang,
              },
            },
            {
              $project: {
                _id: 0,
                name: 1,
                description: 1,
              },
            },
          ],
        },
      },
      {
        $lookup: {
          from: "InvitationAttendee",
          localField: "order.invitationAttendeeId",
          foreignField: "_id",
          as: "invitationAttendee",
        },
      },
      {
        $unwind: {
          path: "$invitationAttendee",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "Invitation",
          localField: "invitationAttendee.invitationId",
          foreignField: "_id",
          as: "invitation",
        },
      },
      {
        $unwind: {
          path: "$invitation",
          preserveNullAndEmptyArrays: true,
        },
      },
      {
        $lookup: {
          from: "InvitationTranslations",
          localField: "invitation._id",
          foreignField: "invitationId",
          as: "invitation.translations",
          pipeline: [
            {
              $lookup: {
                from: "Language",
                localField: "languageId",
                foreignField: "_id",
                as: "language",
              },
            },
            {
              $match: {
                "language.code": lang,
              },
            },
            {
              $project: {
                _id: 0,
                name: 1,
                description: 1,
              },
            },
          ],
        },
      },
      {
        $lookup: {
          from: "Profile",
          localField: "invitationAttendee.contactId",
          foreignField: "_id",
          as: "contact",
        },
      },
      {
        $unwind: {
          path: "$contact",
          preserveNullAndEmptyArrays: true,
        },
      },
      // Also look up the order buyer's profile (for non-invitation orders)
      {
        $lookup: {
          from: "Profile",
          localField: "order.profileId",
          foreignField: "_id",
          as: "orderProfile",
        },
      },
      {
        $unwind: {
          path: "$orderProfile",
          preserveNullAndEmptyArrays: true,
        },
      },
      // Merge: invitation contact takes priority, order profile is fallback
      {
        $addFields: {
          contact: {
            $ifNull: ["$contact", "$orderProfile"],
          },
        },
      },
      {
        $addFields: {
          "orderItems.orderId": { $toString: "$orderItems.orderId" },
        },
      },

      {
        $addFields: {
          "orderItems.ticket.id": { $toString: "$orderItems.ticket._id" },
        },
      },

      {
        $lookup: {
          from: "TicketCategory",
          localField: "orderItems.ticket.categoryId",
          foreignField: "_id",
          as: "category",
        },
      },
      {
        $unwind: {
          path: "$category",
          preserveNullAndEmptyArrays: true,
        },
      },
      ...filtrationStages,
      {
        $group: {
          _id: "$order._id", // Group by the document's unique identifier
          uniqueOrderItemStatuses: { $addToSet: "$orderItems.status" }, // Collect unique statuses
          category: { $addToSet: "$category.name" },
          result: { $first: "$$ROOT" },
          orderItems: { $push: "$orderItems" },
          formResultEntity: { $push: "$formResultEntity" },
          paymentType: { $addToSet: "$payment.method" },
          ticketId: { $addToSet: "$ticket.id" },
          paymentStatuses: {
            $addToSet: "$payment.status",
          },
          orderStatus: { $first: "$order.status" },
        },
      },
    ];

    const footerResultProjectionStages = [
      {
        $addFields: {
          paymentStatuses: {
            $reduce: {
              input: "$paymentStatuses",
              initialValue: [],
              in: { $concatArrays: ["$$value", "$$this"] },
            },
          },
          Holders: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    nameField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "name"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$nameField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.name",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.name",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderEmails: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    emailField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "email"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$emailField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.email",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.email",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderNames: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    nameField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "name"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$nameField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.name",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.name",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderPhones: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    phoneField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "phone"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$phoneField", null] },
                          {
                            $ne: [
                              {
                                $type: "$$entity.formResult.surveyResult.phone",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.phone",
                      null,
                    ],
                  },
                },
              },
            },
          },
          holderCompanies: {
            $map: {
              input: "$formResultEntity",
              as: "entity",
              in: {
                $let: {
                  vars: {
                    companyField: {
                      $arrayElemAt: [
                        {
                          $filter: {
                            input: {
                              $arrayElemAt: [
                                "$$entity.formTemplate.json.pages.elements",
                                0,
                              ],
                            },
                            as: "element",
                            cond: { $eq: ["$$element.type", "company"] },
                          },
                        },
                        0,
                      ],
                    },
                  },
                  in: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$$companyField", null] },
                          {
                            $ne: [
                              {
                                $type:
                                  "$$entity.formResult.surveyResult.company",
                              },
                              "missing",
                            ],
                          },
                        ],
                      },
                      "$$entity.formResult.surveyResult.company",
                      null,
                    ],
                  },
                },
              },
            },
          },
        },
      },
      {
        $addFields: {
          Holder: {
            $reduce: {
              input: {
                $filter: {
                  input: "$Holders",
                  as: "item",
                  cond: { $ne: ["$$item", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
          buyerEmail: {
            $let: {
              vars: {
                // Primary: order-level form
                orderEmailField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$result.orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "email"] },
                      },
                    },
                    0,
                  ],
                },
                // Fallback: first ticket-level form
                ticketEmailField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              {
                                $arrayElemAt: [
                                  "$formResultEntity.formResult.surveyResult",
                                  0,
                                ],
                              },
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "email"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: [
                  "$$orderEmailField.v",
                  {
                    $ifNull: [
                      "$$ticketEmailField.v",
                      { $ifNull: ["$result.contact.email", ""] },
                    ],
                  },
                ],
              },
            },
          },
          buyerName: {
            $let: {
              vars: {
                // Primary: order-level form
                orderNameField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$result.orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "name"] },
                      },
                    },
                    0,
                  ],
                },
                // Fallback: first ticket-level form
                ticketNameField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              {
                                $arrayElemAt: [
                                  "$formResultEntity.formResult.surveyResult",
                                  0,
                                ],
                              },
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "name"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: [
                  "$$orderNameField.v",
                  {
                    $ifNull: [
                      "$$ticketNameField.v",
                      { $ifNull: ["$result.contact.name", ""] },
                    ],
                  },
                ],
              },
            },
          },
          buyerPhone: {
            $let: {
              vars: {
                // Primary: order-level form
                orderPhoneField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$result.orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "phone"] },
                      },
                    },
                    0,
                  ],
                },
                // Fallback: first ticket-level form
                ticketPhoneField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              {
                                $arrayElemAt: [
                                  "$formResultEntity.formResult.surveyResult",
                                  0,
                                ],
                              },
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "phone"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: [
                  "$$orderPhoneField.v",
                  {
                    $ifNull: [
                      "$$ticketPhoneField.v",
                      { $ifNull: ["$result.contact.phoneNumber", ""] },
                    ],
                  },
                ],
              },
            },
          },
          buyerCompany: {
            $let: {
              vars: {
                // Primary: order-level form
                orderCompanyField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              "$result.orderFormResultEntity.formResult.surveyResult",
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "company"] },
                      },
                    },
                    0,
                  ],
                },
                // Fallback: first ticket-level form
                ticketCompanyField: {
                  $arrayElemAt: [
                    {
                      $filter: {
                        input: {
                          $objectToArray: {
                            $ifNull: [
                              {
                                $arrayElemAt: [
                                  "$formResultEntity.formResult.surveyResult",
                                  0,
                                ],
                              },
                              {},
                            ],
                          },
                        },
                        as: "result",
                        cond: { $eq: ["$$result.k", "company"] },
                      },
                    },
                    0,
                  ],
                },
              },
              in: {
                $ifNull: [
                  "$$orderCompanyField.v",
                  {
                    $ifNull: [
                      "$$ticketCompanyField.v",
                      { $ifNull: ["$result.contact.company", ""] },
                    ],
                  },
                ],
              },
            },
          },
        },
      },
      {
        $addFields: {
          dynamicField: {
            // Merge ticket-form fields first, then overlay with order-form fields.
            // This means order-form values always win; ticket-form fills the gaps.
            $mergeObjects: [
              // Ticket-form fields (lower priority)
              {
                $arrayToObject: {
                  $map: {
                    input: {
                      $objectToArray: {
                        $ifNull: [
                          {
                            $arrayElemAt: [
                              "$formResultEntity.formResult.surveyResult",
                              0,
                            ],
                          },
                          {},
                        ],
                      },
                    },
                    as: "field",
                    in: { k: "$$field.k", v: "$$field.v" },
                  },
                },
              },
              // Order-form fields (higher priority — overwrites ticket-form)
              {
                $arrayToObject: {
                  $map: {
                    input: {
                      $objectToArray: {
                        $ifNull: [
                          "$result.orderFormResultEntity.formResult.surveyResult",
                          {},
                        ],
                      },
                    },
                    as: "field",
                    in: {
                      k: {
                        $switch: {
                          branches: [
                            {
                              case: { $eq: ["$$field.k", "name"] },
                              then: "name",
                            },
                            {
                              case: { $eq: ["$$field.k", "email"] },
                              then: "email",
                            },
                            {
                              case: { $eq: ["$$field.k", "phone"] },
                              then: "phone",
                            },
                            {
                              case: { $eq: ["$$field.k", "company"] },
                              then: "company",
                            },
                          ],
                          default: "$$field.k",
                        },
                      },
                      v: "$$field.v",
                    },
                  },
                },
              },
            ],
          },
          isFinalStatus: {
            $in: [
              "$result.order.status",
              ["CANCELED", "EXPIRED", "DELETED", "ON_GOING"],
            ],
          },
          hasPendingApproval: {
            $in: ["PENDING_APPROVAL", "$uniqueOrderItemStatuses"],
          },
          isPartiallyApproved: {
            $and: [
              {
                $in: ["APPROVED", "$uniqueOrderItemStatuses"],
              },
              {
                $in: ["PENDING_APPROVAL", "$uniqueOrderItemStatuses"],
              },
            ],
          },
          allConfirmed: {
            $and: [
              { $gt: [{ $size: "$orderItems" }, 0] },
              {
                $allElementsTrue: {
                  $map: {
                    input: "$orderItems",
                    as: "item",
                    in: { $eq: ["$$item.status", "CONFIRMED"] },
                  },
                },
              },
            ],
          },
          // Paid / partially-paid / unpaid derivation shared with
          // `calculateOrderStatus`: ignores FAILED/EXPIRED retry attempts so a
          // `[FAILED, PAID]` order is reported as PAID/COMPLETED.
          ...buildPaymentSettlementExpressions("$paymentStatuses"),
          allRejected: {
            $and: [
              { $gt: [{ $size: "$uniqueOrderItemStatuses" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: [
                        "$uniqueOrderItemStatuses",
                        ["REJECTED"],
                      ],
                    },
                  },
                  { $size: "$uniqueOrderItemStatuses" },
                ],
              },
            ],
          },
          allRequestRevision: {
            $and: [
              { $gt: [{ $size: "$uniqueOrderItemStatuses" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: [
                        "$uniqueOrderItemStatuses",
                        ["REQUEST_REVISION"],
                      ],
                    },
                  },
                  { $size: "$uniqueOrderItemStatuses" },
                ],
              },
            ],
          },
          allApproved: {
            $and: [
              { $gt: [{ $size: "$uniqueOrderItemStatuses" }, 0] },
              {
                $eq: [
                  {
                    $size: {
                      $setIntersection: [
                        "$uniqueOrderItemStatuses",
                        ["APPROVED"],
                      ],
                    },
                  },
                  { $size: "$uniqueOrderItemStatuses" },
                ],
              },
            ],
          },
        },
      },
      {
        $addFields: {
          virtualStatus: {
            $cond: {
              // Treat CANCELED/EXPIRED/DELETED as terminal, but do NOT let an
              // in-progress ON_GOING order short-circuit when it already has
              // payment records: derive the status from those payments instead
              // (PAID / AWAITING_PAYMENT / FAILED / EXPIRED).
              if: {
                $and: [
                  "$isFinalStatus",
                  {
                    $not: {
                      $and: [
                        { $eq: ["$result.order.status", "ON_GOING"] },
                        { $gt: [{ $size: "$paymentStatuses" }, 0] },
                      ],
                    },
                  },
                ],
              },
              then: "$result.order.status",
              else: {
                $cond: {
                  if: "$hasPendingApproval",
                  then: "PENDING_APPROVAL",
                  else: {
                    $cond: {
                      if: "$isPartiallyApproved",
                      then: "PARTIALLY_APPROVED",
                      else: {
                        $cond: {
                          if: { $and: ["$allConfirmed", "$allPaid"] },
                          then: "COMPLETED",
                          else: {
                            $cond: {
                              if: { $and: ["$allConfirmed", "$anyPaid"] },
                              then: "PARTIALLY_PAID",
                              else: {
                                $cond: {
                                  if: "$allRejected",
                                  then: "REJECTED",
                                  else: {
                                    $cond: {
                                      if: "$allRequestRevision",
                                      then: "REQUEST_REVISION",
                                      else: {
                                        $cond: {
                                          if: "$allApproved",
                                          then: "APPROVED",
                                          else: {
                                            $cond: {
                                              if: "$allPaid",
                                              then: "PAID",
                                              else: {
                                                $cond: {
                                                  if: "$anyPaid",
                                                  then: "PARTIALLY_PAID",
                                                  else: {
                                                    $cond: {
                                                      if: "$allUnpaid",
                                                      then: "AWAITING_PAYMENT",
                                                      else: {
                                                        $cond: {
                                                          if: "$hasOnlySupersededPayments",
                                                          then: "$supersededPaymentStatus",
                                                          else: "ON_GOING",
                                                        },
                                                      },
                                                    },
                                                  },
                                                },
                                              },
                                            },
                                          },
                                        },
                                      },
                                    },
                                  },
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
          Quantity: { $size: "$orderItems" },
          uniqueTicketTypes: {
            $reduce: {
              input: "$orderItems",
              initialValue: [],
              in: {
                $let: {
                  vars: {
                    typeStr: {
                      $let: {
                        vars: {
                          privacy: {
                            $ifNull: ["$$this.ticket.privacy", "-"],
                          },
                        },
                        in: {
                          $cond: [
                            {
                              $and: [
                                { $ne: ["$$this.ticket", null] },
                                {
                                  $eq: ["$$this.ticket.requireApproval", true],
                                },
                              ],
                            },
                            { $concat: ["$$privacy", " (by request)"] },
                            "$$privacy",
                          ],
                        },
                      },
                    },
                  },
                  in: {
                    $cond: [
                      { $in: ["$$typeStr", "$$value"] },
                      "$$value",
                      { $concatArrays: ["$$value", ["$$typeStr"]] },
                    ],
                  },
                },
              },
            },
          },
        },
      },
      {
        $project: {
          // Technical fields needed for dynamic logic
          id: { $toString: "$result.order._id" },
          orderId: { $toString: "$result.order._id" },
          orderCreatedAtMs: {
            $convert: {
              input: "$result.order.createdAt",
              to: "long",
              onError: 0,
              onNull: 0,
            },
          },
          virtualStatus: 1,
          dynamicField: 1,
          paymentStatuses: 1,
          payment: "$result.payment",
          uniqueOrderItemStatuses: 1,
          orderItems: 1,
          formResultEntity: "$formResultEntity",
          orderFormResultEntity: "$result.orderFormResultEntity",
          createdByAdmin: "$result.createdByAdmin",
          "Created By": {
            $ifNull: ["$result.createdByAdmin.name", ""],
          },

          // 1. Order Info
          Created: {
            $ifNull: [
              {
                $dateToString: {
                  format: "%Y-%m-%d %H:%M:%S",
                  date: "$result.order.createdAt",
                  timezone: eventTimeZone,
                },
              },
              "",
            ],
          },
          "Order ID": { $toString: "$result.order.number" },
          "Ticket Type": {
            $reduce: {
              input: {
                $filter: {
                  input: "$category",
                  as: "cat",
                  cond: { $ne: ["$$cat", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: {
                  if: { $eq: ["$$value", ""] },
                  then: "$$this",
                  else: { $concat: ["$$value", ", ", "$$this"] },
                },
              },
            },
          },
          Lang: { $ifNull: ["$result.order.lang", "en"] },
          "Invoice No": { $ifNull: ["$result.invoice.number", ""] },
          Quantity: 1,
          Currency: {
            $ifNull: [
              {
                $cond: [
                  { $eq: ["$result.order.total", 0] },
                  "-",
                  "$result.order.currency",
                ],
              },
              "",
            ],
          },
          Total: {
            $cond: [
              { $eq: [{ $size: { $ifNull: ["$result.payment", []] } }, 0] },
              "Free",
              {
                $let: {
                  vars: {
                    totalPayments: {
                      $sum: {
                        $map: {
                          input: { $ifNull: ["$result.payment", []] },
                          as: "p",
                          in: { $ifNull: ["$$p.grandTotal", 0] },
                        },
                      },
                    },
                    refundTotal: {
                      $ifNull: ["$result.invoice.refundTotal", 0],
                    },
                  },
                  in: {
                    $let: {
                      vars: {
                        netTotal: {
                          $subtract: ["$$totalPayments", "$$refundTotal"],
                        },
                      },
                      in: {
                        $cond: [
                          {
                            $and: [
                              { $eq: ["$$totalPayments", 0] },
                              { $eq: ["$$refundTotal", 0] },
                            ],
                          },
                          "Free",
                          "$$netTotal",
                        ],
                      },
                    },
                  },
                },
              },
            ],
          },
          Tax: {
            $cond: [
              { $eq: [{ $size: "$result.payment" }, 0] },
              "-",
              {
                $let: {
                  vars: {
                    totalTax: { $sum: "$result.payment.totalTax" },
                  },
                  in: {
                    $cond: [{ $eq: ["$$totalTax", 0] }, "-", "$$totalTax"],
                  },
                },
              },
            ],
          },
          "Payment Type": {
            $cond: {
              if: {
                $or: [
                  {
                    $eq: [{ $size: { $ifNull: ["$result.payment", []] } }, 0],
                  },
                  { $eq: ["$result.order.total", 0] },
                ],
              },
              then: "FREE",
              else: {
                $ifNull: [{ $arrayElemAt: ["$result.payment.method", 0] }, "-"],
              },
            },
          },
          "Payment Status": {
            $ifNull: [
              { $arrayElemAt: ["$result.payment.status", 0] },
              "-",
            ],
          },
          Status: "$result.order.status",

          // 2. Buyer Info (Manual fallbacks - dynamicFields will be merged later)
          "Buyer Name": {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerName", null] },
                  { $ne: ["$buyerName", ""] },
                ],
              },
              then: "$buyerName",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.name", null] },
                      { $ne: ["$result.contact.name", ""] },
                    ],
                  },
                  then: "$result.contact.name",
                  else: "",
                },
              },
            },
          },
          "Buyer Email": {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerEmail", null] },
                  { $ne: ["$buyerEmail", ""] },
                ],
              },
              then: "$buyerEmail",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.email", null] },
                      { $ne: ["$result.contact.email", ""] },
                    ],
                  },
                  then: "$result.contact.email",
                  else: "",
                },
              },
            },
          },
          "Buyer Phone": {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerPhone", null] },
                  { $ne: ["$buyerPhone", ""] },
                ],
              },
              then: "$buyerPhone",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.phoneNumber", null] },
                      { $ne: ["$result.contact.phoneNumber", ""] },
                    ],
                  },
                  then: "$result.contact.phoneNumber",
                  else: "",
                },
              },
            },
          },
          "Buyer Company": {
            $cond: {
              if: {
                $and: [
                  { $ne: ["$buyerCompany", null] },
                  { $ne: ["$buyerCompany", ""] },
                ],
              },
              then: "$buyerCompany",
              else: {
                $cond: {
                  if: {
                    $and: [
                      { $ne: ["$result.contact.company", null] },
                      { $ne: ["$result.contact.company", ""] },
                    ],
                  },
                  then: "$result.contact.company",
                  else: "",
                },
              },
            },
          },

          // 3. Event Info
          "Event Name": { $ifNull: ["$result.eventName", ""] },
          "Event ID": "$result.eventId",
          "Start Date": { $ifNull: ["$result.startDate", null] },
          "End Date": { $ifNull: ["$result.endDate", null] },

          // 4. Form Info (Ticket Holder)
          "Ticket Holder Name": {
            $reduce: {
              input: {
                $filter: {
                  input: "$holderNames",
                  as: "name",
                  cond: { $ne: ["$$name", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
          "Ticket Holder Email": {
            $reduce: {
              input: {
                $filter: {
                  input: "$holderEmails",
                  as: "email",
                  cond: { $ne: ["$$email", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
          "Ticket Holder Phone": {
            $reduce: {
              input: {
                $filter: {
                  input: "$holderPhones",
                  as: "phone",
                  cond: { $ne: ["$$phone", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },
          "Ticket Holder Company": {
            $reduce: {
              input: {
                $filter: {
                  input: "$holderCompanies",
                  as: "company",
                  cond: { $ne: ["$$company", null] },
                },
              },
              initialValue: "",
              in: {
                $cond: [
                  { $eq: ["$$value", ""] },
                  "$$this",
                  { $concat: ["$$value", ", ", "$$this"] },
                ],
              },
            },
          },

          // Extra fields
          ...(isDTCMEvent
            ? {
                DTCMPriceCategory:
                  "$orderItems.ticket.dtcmTicketPrice.category.name",
                DTCMTicketPrice: "$orderItems.ticket.dtcmTicketPrice.typeCode",
              }
            : {}),
          _id: 0,
        },
      },
      ...(deselectedColumns
        ? [
            {
              $project: {
                Holder: 0,
                uniqueOrderItemStatuses: 0,
                orderItems: 0,
                orderFormResultEntity: 0,
                formResultEntity: 0,
                "Order Items": 0,
                paymentStatuses: 0,
                payment: 0,
                ticket: 0,
                orderId: 0,
                virtualStatus: 0,
              },
            },
            {
              $addFields: {
                flattenedDynamicFields: {
                  $objectToArray: "$dynamicField",
                },
                "Payment Type": {
                  $cond: {
                    if: { $isArray: "$Payment Type" },
                    then: {
                      $reduce: {
                        input: "$Payment Type",
                        initialValue: "",
                        in: {
                          $cond: [
                            { $eq: ["$$value", ""] },
                            "$$this",
                            { $concat: ["$$value", ", ", "$$this"] },
                          ],
                        },
                      },
                    },
                    else: "$Payment Type",
                  },
                },
              },
            },
            {
              $replaceRoot: {
                newRoot: {
                  $mergeObjects: ["$$ROOT", "$dynamicField"],
                },
              },
            },
            {
              $project: {
                dynamicField: 0,
                flattenedDynamicFields: 0,
                name: 0,
                email: 0,
                phone: 0,
                company: 0,
                ...deselectedColumns,
              },
            },
          ]
        : exported
        ? [
            {
              $addFields: {
                flattenedDynamicFields: {
                  $objectToArray: "$dynamicField",
                },
                "Payment Type": {
                  $cond: {
                    if: { $isArray: "$Payment Type" },
                    then: {
                      $reduce: {
                        input: "$Payment Type",
                        initialValue: "",
                        in: {
                          $cond: [
                            { $eq: ["$$value", ""] },
                            "$$this",
                            { $concat: ["$$value", ", ", "$$this"] },
                          ],
                        },
                      },
                    },
                    else: "$Payment Type", // If not array, use original
                  },
                },
              },
            },
            {
              $replaceRoot: {
                newRoot: {
                  $mergeObjects: ["$$ROOT", "$dynamicField"],
                },
              },
            },

            {
              $project: {
                Holder: 0,
                uniqueOrderItemStatuses: 0,
                orderItems: 0,
                orderFormResultEntity: 0,
                formResultEntity: 0,
                paymentStatus: 0,
                payment: 0,
                ticket: 0,
                dynamicField: 0,
                flattenedDynamicFields: 0,
                "Order Items": 0,
                orderId: 0,
                virtualStatus: 0,
                paymentStatuses: 0,
                name: 0,
                email: 0,
                phone: 0,
                company: 0,
              },
            },
          ]
        : []),
    ];
    const orderStages = useEarlyOrderPagination
      ? [
          ...stagesBeforeOrderItems,
          {
            $facet: {
              pagination: [
                {
                  $group: {
                    _id: null,
                    total: { $sum: 1 },
                  },
                },
                {
                  $addFields: {
                    hasNextPage: {
                      $literal: false,
                    },
                    hasPreviousPage: {
                      $literal: false,
                    },
                  },
                },
              ],
              pageProbe: paginate
                ? [
                    // Probe page size BEFORE unwinding orderItems (orders with 0 items
                    // are dropped later by `$unwind: "$orderItems"`). This prevents
                    // false `hasNextPage: false` when the extra (limit+1) order has no items.
                    { $sort: { "order.createdAt": -1, "order._id": -1 } },
                    ...(useEarlyForwardKeyset &&
                    ks.cursorId &&
                    ks.cursorTsMs != null
                      ? [
                          buildOrderForwardCompositeMatch(
                            "order",
                            ks.cursorId,
                            ks.cursorTsMs
                          ),
                        ]
                      : []),
                    ...(useEarlyReverseKeyset &&
                    ks.beforeCursor &&
                    ks.beforeTsMs != null
                      ? [
                          buildOrderReverseCompositeMatch(
                            "order",
                            ks.beforeCursor,
                            ks.beforeTsMs
                          ),
                        ]
                      : []),
                    ...(paginate && useEarlyReverseKeyset
                      ? [
                          { $sort: { "order.createdAt": 1, "order._id": 1 } },
                          { $limit: pageLimit },
                        ]
                      : paginate
                      ? [{ $limit: pageLimit }]
                      : []),
                    { $group: { _id: null, count: { $sum: 1 } } },
                  ]
                : [{ $limit: 0 }],
              result: [
                // Sort newest orders first; use _id as deterministic tie-breaker
                { $sort: { "order.createdAt": -1, "order._id": -1 } },
                ...(useEarlyForwardKeyset &&
                ks.cursorId &&
                ks.cursorTsMs != null
                  ? [
                      buildOrderForwardCompositeMatch(
                        "order",
                        ks.cursorId,
                        ks.cursorTsMs
                      ),
                    ]
                  : []),
                ...(useEarlyReverseKeyset &&
                ks.beforeCursor &&
                ks.beforeTsMs != null
                  ? [
                      buildOrderReverseCompositeMatch(
                        "order",
                        ks.beforeCursor,
                        ks.beforeTsMs
                      ),
                    ]
                  : []),
                ...(paginate && useEarlyReverseKeyset
                  ? [
                      { $sort: { "order.createdAt": 1, "order._id": 1 } },
                      { $limit: pageLimit },
                      { $sort: { "order.createdAt": -1, "order._id": -1 } },
                    ]
                  : paginate
                  ? [{ $limit: pageLimit }]
                  : []),
                ...stagesFromOrderItems,
                // After expanding orderItems, enforce stable row ordering too
                {
                  $sort: {
                    "order.createdAt": -1,
                    "order._id": -1,
                    "orderItems._id": -1,
                  },
                },
                ...footerResultProjectionStages,
              ],
            },
          },
        ]
      : [...stagesBeforeOrderItems, ...stagesFromOrderItems];

    const footerStages = useEarlyOrderPagination
      ? []
      : [
          {
            $facet: {
              pagination: [
                {
                  $group: {
                    _id: null,
                    total: { $sum: 1 },
                  },
                },
                {
                  $addFields: {
                    hasNextPage: {
                      $literal: false,
                    },
                    hasPreviousPage: {
                      $literal: false,
                    },
                  },
                },
              ],
              result: [
                {
                  $sort: {
                    // Sort newest orders first; use _id as deterministic tie-breaker
                    "result.order.createdAt": -1,
                    "result.order._id": -1,
                    "result.orderItems._id": -1,
                  },
                },
                ...(useFooterForwardKeyset &&
                ks.cursorId &&
                ks.cursorTsMs != null
                  ? [
                      buildOrderForwardCompositeMatch(
                        "result.order",
                        ks.cursorId,
                        ks.cursorTsMs
                      ),
                    ]
                  : []),
                ...(useFooterReverseKeyset &&
                ks.beforeCursor &&
                ks.beforeTsMs != null
                  ? [
                      buildOrderReverseCompositeMatch(
                        "result.order",
                        ks.beforeCursor,
                        ks.beforeTsMs
                      ),
                    ]
                  : []),
                ...(paginate && useFooterReverseKeyset
                  ? [
                      {
                        $sort: {
                          "result.order.createdAt": 1,
                          "result.order._id": 1,
                          "result.orderItems._id": 1,
                        },
                      },
                      { $limit: pageLimit },
                      {
                        $sort: {
                          "result.order.createdAt": -1,
                          "result.order._id": -1,
                          "result.orderItems._id": -1,
                        },
                      },
                    ]
                  : paginate
                  ? [{ $limit: pageLimit }]
                  : []),
                ...footerResultProjectionStages,
              ],
            },
          },
        ];
    const stages = [
      {
        $match: {
          $expr: {
            $cond: {
              if: { $eq: [type, REPORT_TYPE.EVENT] },
              then: { $eq: ["$_id", { $oid: id }] },
              else: {
                $cond: {
                  if: { $eq: [type, REPORT_TYPE.ORGANIZATION] }, // The 'else if' condition
                  //{
                  then: {
                    $and: [
                      { $eq: [{ $type: "$organizationIds" }, "array"] },
                      {
                        // Match even if organizationIds contains ObjectIds (expected) or strings (legacy).
                        $in: [
                          { $toString: id },
                          {
                            $map: {
                              input: "$organizationIds",
                              as: "orgId",
                              in: { $toString: "$$orgId" },
                            },
                          },
                        ],
                      },
                    ],
                  },
                  else: { $eq: ["$whiteLabelId", { $oid: id }] }, // The final 'else' action
                },
              },
            },
          },
        },
      },
      {
        $lookup: {
          from: "EventTranslations",
          localField: "_id",
          foreignField: "eventId",
          as: "event.eventTranslations",
          pipeline: [
            {
              $lookup: {
                from: "Language",
                localField: "languageId",
                foreignField: "_id",
                as: "language",
              },
            },
            // Only filter by language when lang is actually provided;
            // otherwise return all translations so eventName is never empty.
            ...(lang
              ? [
                  {
                    $match: {
                      "language.code": lang,
                    },
                  },
                ]
              : []),
            {
              $project: {
                _id: 0,
                name: 1,
                description: 1,
              },
            },
          ],
        },
      },
      {
        $project: {
          eventId: { $toString: "$_id" },
          eventName: {
            $arrayElemAt: ["$event.eventTranslations.name", 0],
          },
          startDate: {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$startDate",
              timezone: eventTimeZone,
            },
          },
          endDate: {
            $dateToString: {
              format: "%Y-%m-%d %H:%M:%S",
              date: "$endDate",
              timezone: eventTimeZone,
            },
          },
        },
      },

      ...orderStages,
      ...footerStages,
    ];
    const result = (await prisma.event.aggregateRaw({
      pipeline: [...stages],
    })) as unknown as ReportData[];

    type OrderReportPageProbe = { count: number };
    type OrderReportResponse = ReportData & { pageProbe?: OrderReportPageProbe[] };
    const response = result[0] as OrderReportResponse | undefined;
    if (response?.pagination?.[0]) {
      const fetchedRows = Array.isArray(response.result) ? response.result : [];
      const probeCount = response.pageProbe?.[0]?.count;
      const fetchedExtra =
        paginate &&
        limit > 0 &&
        (typeof probeCount === "number" && Number.isFinite(probeCount)
          ? probeCount > limit
          : fetchedRows.length > limit);
      const reverseKeyset = useEarlyReverseKeyset || useFooterReverseKeyset;
      const forwardKeyset = useEarlyForwardKeyset || useFooterForwardKeyset;

      response.pagination[0].limit = limit;
      response.pagination[0].hasPreviousPage = reverseKeyset
        ? fetchedExtra
        : forwardKeyset;
      response.pagination[0].hasNextPage = reverseKeyset ? true : fetchedExtra;

      if (paginate && limit > 0) {
        response.result =
          reverseKeyset && fetchedExtra
            ? fetchedRows.slice(1)
            : fetchedRows.slice(0, limit);
      }
    }

    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export { buildOrderLookupSubpipeline } from "./orderLookupStages";

type Pagination = {
  total: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  limit?: number;
};
export type ReportData = {
  pagination: Pagination[];
  result: { [key: string]: any }[];
};
